import React, { useState, useEffect, useCallback } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      company: 'Founder, EcoBloom Cosmetics',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&q=80',
      rating: 5,
      text: 'TrademarkPro made the entire process seamless. Their team handled everything professionally, and I received my trademark registration in just 6 months. Highly recommend!',
    },
    {
      name: 'Michael Chen',
      company: 'CEO, TechVision Solutions',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&q=80',
      rating: 5,
      text: 'The AI-powered search tool caught potential conflicts I never would have found. Their attorneys were knowledgeable and responsive throughout the entire filing process.',
    },
    {
      name: 'Jessica Rodriguez',
      company: 'Owner, Artisan Coffee Roasters',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&q=80',
      rating: 5,
      text: 'Best investment I made for my business. The peace of mind knowing my brand is protected is invaluable. The process was straightforward and affordable.',
    },
    {
      name: 'David Park',
      company: 'Co-founder, FitLife App',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&q=80',
      rating: 5,
      text: 'From start to finish, the TrademarkPro team was there for us. They explained every step clearly and responded to our office action quickly. Excellent service!',
    },
    {
      name: 'Amanda Foster',
      company: 'Creative Director, Bold Designs',
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&q=80',
      rating: 5,
      text: 'I appreciated their transparency with pricing and timeline. No surprises, no hidden fees. They delivered exactly what they promised and more.',
    },
    {
      name: 'Robert Thompson',
      company: 'Founder, Urban Garden Supply',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&q=80',
      rating: 5,
      text: 'Their monitoring service has already alerted me to two potential infringements. TrademarkPro continues to protect my brand long after registration. Worth every penny!',
    },
  ];

  const total = testimonials.length;

  const next = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  // Auto-slide
  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const getIndex = (offset) => (currentIndex + offset + total) % total;

  // Get 5 visible cards: -2, -1, center, +1, +2
  const positions = [-2, -1, 0, 1, 2];

  return (
    <div className="py-16 md:py-20 relative overflow-hidden" data-testid="testimonials-section">
      {/* Themed background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E] via-[#1a0f5e] to-[#0d0830]"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#FF035B]/8 rounded-full blur-2xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Trusted by Thousands of Businesses
          </h2>
          <p className="text-base text-white/60 max-w-2xl mx-auto">
            See what our clients have to say about their trademark registration experience
          </p>
        </div>

        {/* Desktop Carousel — center focused */}
        <div className="hidden md:block relative" style={{ height: '340px' }}>
          <div className="absolute inset-0 flex items-center justify-center">
            {positions.map((offset) => {
              const idx = getIndex(offset);
              const t = testimonials[idx];
              const isCenter = offset === 0;
              const isNear = Math.abs(offset) === 1;

              let translateX = '0%';
              let scale = 1;
              let opacity = 1;
              let zIndex = 10;
              let blur = '0px';

              if (offset === -2) { translateX = '-145%'; scale = 0.7; opacity = 0.3; zIndex = 1; blur = '4px'; }
              else if (offset === -1) { translateX = '-72%'; scale = 0.85; opacity = 0.6; zIndex = 5; blur = '2px'; }
              else if (offset === 0) { translateX = '0%'; scale = 1; opacity = 1; zIndex = 10; blur = '0px'; }
              else if (offset === 1) { translateX = '72%'; scale = 0.85; opacity = 0.6; zIndex = 5; blur = '2px'; }
              else if (offset === 2) { translateX = '145%'; scale = 0.7; opacity = 0.3; zIndex = 1; blur = '4px'; }

              return (
                <div
                  key={`${offset}-${idx}`}
                  className="absolute transition-all duration-700 ease-in-out cursor-pointer"
                  style={{
                    transform: `translateX(${translateX}) scale(${scale})`,
                    opacity,
                    zIndex,
                    filter: `blur(${blur})`,
                    width: '420px',
                  }}
                  onClick={() => { if (offset !== 0) setCurrentIndex(idx); }}
                >
                  <div className={`rounded-2xl p-6 border ${isCenter ? 'bg-white border-[#FF035B]/30 shadow-2xl shadow-[#FF035B]/20' : 'bg-white/90 border-white/20 shadow-lg'}`}>
                    {/* Stars */}
                    <div className="flex gap-1 mb-3">
                      {[...Array(t.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#FF035B] text-[#FF035B]" />
                      ))}
                    </div>
                    {/* Text */}
                    <p className="text-gray-700 text-sm leading-relaxed mb-5 line-clamp-4">
                      "{t.text}"
                    </p>
                    {/* Profile */}
                    <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                      <img
                        src={t.image}
                        alt={t.name}
                        className={`w-12 h-12 rounded-full object-cover ring-2 ${isCenter ? 'ring-[#FF035B]' : 'ring-gray-200'}`}
                      />
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{t.name}</div>
                        <div className="text-xs text-gray-500">{t.company}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Nav arrows */}
          <button
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all"
            data-testid="testimonial-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all"
            data-testid="testimonial-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile — single card */}
        <div className="md:hidden">
          <div className="px-6">
            {(() => {
              const t = testimonials[currentIndex];
              return (
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-[#FF035B]/20">
                  <div className="flex gap-1 mb-3">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#FF035B] text-[#FF035B]" />
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-5">"{t.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <img src={t.image} alt={t.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-[#FF035B]" />
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{t.name}</div>
                      <div className="text-xs text-gray-500">{t.company}</div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Mobile arrows — below card */}
          <div className="flex justify-center gap-6 mt-5">
            <button onClick={prev} className="w-10 h-10 bg-[#FF035B] rounded-full flex items-center justify-center text-white shadow-lg" data-testid="testimonial-mobile-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={next} className="w-10 h-10 bg-[#FF035B] rounded-full flex items-center justify-center text-white shadow-lg" data-testid="testimonial-mobile-next">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                currentIndex === index ? 'bg-[#FF035B] w-8' : 'bg-white/30 w-2.5 hover:bg-white/50'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSection;
